import { Card, Checkbox } from '@/components_deprecated'
import {
  Combobox,
  UserCombobox,
  useHideBasedOnLeaderGroup,
} from '@/components/combobox'
import { MonthYearPicker } from '@/components/month-year-picker'
import { PageType } from '@/modules/common/consts'
import { Field } from '@/modules/companies'
import { getNodes } from '@/utils/graphql'
import { usePermissions, useUser, useLeaderGroups } from '@/utils/hooks'
import { useInfoModal } from '@/utils/hooks/useInfoModal/useInfoModal'
import { PERMISSIONS } from '@/utils/permissions'
import { useQuery } from '@apollo/client'
import { UserWorkPeriodsWarningCard } from './UserWorkPeriodsWarningCard'
import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import { endOfMonth, formatISO, startOfMonth } from 'date-fns'
import React from 'react'
import { isMobileOnly } from 'react-device-detect'
import styled from 'styled-components'
import {
  WORK_PERIODS,
  SALARY_MONTH_SUMMARY,
  REMAINING_WORK_PERIOD_RESOLVING,
  REST_PERIODS,
} from './queries'
import { WorkPeriodApproveCards } from './WorkPeriodApproveCards'
import { WorkPeriodApproveTable } from './WorkPeriodApproveTable'
import { H1 } from '@/components'
import { getApprovalRows } from './approval-rows'
import {
  WORK_PERIOD_FILTER_STORAGE,
  workPeriodFilterDefaults,
  workPeriodFilterTransforms,
} from './work-period-filter-storage'
import { SalaryMonthSummaryCards } from './salary-month-summary-cards'
import { aggregateMonthSummary } from './utils/aggregate-month-summary'
import { useSalaryModuleConfiguration } from './hooks/useSalaryModuleConfiguration'
import { useSalaryApproveColumns } from './hooks/useSalaryApproveColumns'
import { ColumnNames } from '@/modules/dynamic_tables'

const Wrapper = styled.div`
  ${props => props.theme.layout.default};
  position: relative;
`

const InfoContentWrapper = styled.div`
  display: flex;
  gap: 0.5rem;
`

const Controls = styled(Card)`
  display: grid;
  grid-template-columns: 1fr auto;

  margin-bottom: 1rem;
  padding: 0;

  section {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    padding: 1rem;

    & > :first-child {
      flex-grow: 1;
    }

    & + section {
      border-left: 1px solid ${props => props.theme.colors.gray8};
      align-items: center;
    }
  }

  ${props => props.theme.media.mobile} {
    grid-template-columns: 1fr;

    section {
      flex-direction: column;
      padding: 1rem;

      & + section {
        padding-top: 0;
        border-left: 0;
        align-items: stretch;
      }
    }
  }
`

export const WorkPeriodApproveView: React.FC = () => {
  const translations = useTranslate({
    salaryCode: 'common.salary-code',
    amount: 'common.amount',
    activities: 'common.activities',
    workDay: 'common.work-day',
    dayRest: 'common.day-rest',
    weekRest: 'common.week-rest',
    showRest: 'common.show-rest',
    export: 'common.export',
    weekN: ['common.week-n', { n: 0 }],
    workOverview: 'salary.work-overview',

    nUsersNeedApproval: ['salary.n-users-need-approval', { n: 0 }],
    oneUserNeedsApproval: 'salary.one-user-needs-approval',
    nWorkPeriods: ['salary.n-work-periods', { n: 0 }],

    sumForMonth: ['activities.sum-for-month-title', { month: '' }],
    driving: 'activities.driving',
    otherWork: 'common.other-work',
    overtime: 'common.overtime',
    available: 'activities.availability-time',
    lockedValue: 'salary.locked-value',
    lock: 'salary.lock',

    toast: {
      mailSuccess: 'exports.success-email-process',
    },
    error: {
      generic: 'error.generic-server-error',
    },

    salaryMode: 'common.salary-mode',
    month: 'common.month',
    user: 'common.user',
    chooseGroup: 'common.choose-group',

    overtimeThatOverlapsMonths: 'salary.overtime-that-overlaps-months',
    nextMonth: 'common.next-month',
    workPeriods: 'salary.work-periods',
    confirmHours: 'salary.confirm-hours',

    showResolved: 'salary.show-resolved',
    showPlannedWork: 'salary.show-planned-work',

    leaderGroups: 'companies.leader-groups',
  })

  const me = useUser()
  const { hasPermissions } = usePermissions()
  const canSeeOthers = hasPermissions(
    PERMISSIONS.workPeriod.view.othersworkperiod,
    true
  )
  const { InfoModal, infoContent, InfoButton } = useInfoModal(
    PageType.CONFIRM_HOURS
  )

  const leaderGroups = useLeaderGroups()
  const { hasAccess, myLeaderGroups } = leaderGroups
  const { driverApprove, managerApprove } = useSalaryModuleConfiguration()
  const hasApprovalFlow = driverApprove || managerApprove

  const form = useForm({
    values: workPeriodFilterDefaults(),
    config: {
      storage: {
        storeFormState: true,
        retrieveFormStateOnMount: true,
        sessionStorage: true,
        storeFormStateName: WORK_PERIOD_FILTER_STORAGE,
        transformValues: workPeriodFilterTransforms(canSeeOthers),
      },
    },
  })

  const hideUserItem = useHideBasedOnLeaderGroup(
    form.formValues.leaderGroup ?? '',
    leaderGroups
  )

  const datetimeStart = startOfMonth(form.formValues.month)
  const datetimeEnd = endOfMonth(form.formValues.month)

  const effectiveUser = form.formValues.user ?? (canSeeOthers ? null : me.id)
  const queryVariables = {
    user: effectiveUser,
    datetimeStart: formatISO(datetimeStart),
    datetimeEnd: formatISO(datetimeEnd),
  }

  // The optional columns decide what the list asks for, so it waits for the
  // column settings rather than fetching once without them and again with.
  const approveColumns = useSalaryApproveColumns()
  const {
    data,
    loading: workPeriodsLoading,
    refetch,
  } = useQuery(WORK_PERIODS, {
    skip: !approveColumns.loaded,
    variables: {
      ...queryVariables,
      withWorkTimes:
        approveColumns.isEnabled(ColumnNames.TOTAL_WORK) ||
        approveColumns.isEnabled(ColumnNames.PAUSE),
      withDepartments: approveColumns.isEnabled(ColumnNames.DEPARTMENT),
      withFacilities: approveColumns.isEnabled(ColumnNames.FACILITY),
    },
    fetchPolicy: 'cache-and-network',
  })
  const loading = workPeriodsLoading || !approveColumns.loaded

  // `cache-and-network` lets a revisited month/user render instantly from
  // cache while a background refetch updates with fresh values. When the
  // cache misses (first visit) `data` is undefined → the summary card
  // renders its built-in skeleton.
  const { data: monthSummary, refetch: refetchMonthSummary } = useQuery(
    SALARY_MONTH_SUMMARY,
    {
      skip: !effectiveUser,
      fetchPolicy: 'cache-and-network',
      variables: {
        user: effectiveUser ?? '',
        datetimeStart: queryVariables.datetimeStart,
        datetimeEnd: queryVariables.datetimeEnd,
      },
    }
  )

  // A rest is the gap between one driver's consecutive shifts, so it only
  // means anything once a single driver is in view -- interleaved with other
  // drivers' rows it would connect shifts that have nothing to do with each
  // other. The all-users list therefore never asks for rests at all.
  const showDayRest = form.formValues.showDayRest !== false
  const showWeekRest = form.formValues.showWeekRest !== false
  const restRowsAvailable = !!effectiveUser
  const { data: restData, refetch: refetchRests } = useQuery(REST_PERIODS, {
    skip: !restRowsAvailable || (!showDayRest && !showWeekRest),
    fetchPolicy: 'cache-and-network',
    variables: {
      user: effectiveUser ?? '',
      datetimeStart: queryVariables.datetimeStart,
      datetimeEnd: queryVariables.datetimeEnd,
    },
  })

  // The "N users need approval" banner is company-wide (no user filter) and
  // far cheaper than the work-period list, so it runs as its own query and
  // paints independently instead of blocking on the heavy list. Only fetched
  // when the viewer can see other users and the company actually runs the
  // leader approval step (the only case the banner renders).
  const showRemainingBanner = canSeeOthers && managerApprove
  const { data: remainingData, refetch: refetchRemaining } = useQuery(
    REMAINING_WORK_PERIOD_RESOLVING,
    {
      skip: !showRemainingBanner,
      fetchPolicy: 'cache-and-network',
      variables: {
        datetimeStart: queryVariables.datetimeStart,
        datetimeEnd: queryVariables.datetimeEnd,
      },
    }
  )

  // Mutations from the table/cards call `initRefetch` after editing work
  // periods, diets, leader groups, transfer-to-flex, etc. All queries
  // must refresh, otherwise the summary cards / banner keep showing stale
  // totals.
  // Approvals are the exception: the mutation response already updates the
  // row in the cache, so only the totals refresh. Refetching the full list on
  // every click queues the heavy month query behind each approval.
  const refetchApprovalTotals = () => {
    if (effectiveUser) void refetchMonthSummary()
    if (showRemainingBanner) void refetchRemaining()
  }
  const refetchAll = () => {
    void refetch()
    if (restRowsAvailable) void refetchRests()
    refetchApprovalTotals()
  }

  const workPeriods = getNodes(data?.allWorkPeriods)
  const calendarItems = getNodes(data?.allCalendarItems)
  const plannedWorkFilterEnabled = form.formValues.showPlannedWork !== false
  // Show the "Show planned work" checkbox and the planned calendar
  // items as rows if any work period uses planner overtime, or when
  // only planned shifts (no work logged yet) are returned.
  const anyWorkPeriodUsesPlannerForOvertime = workPeriods.some(
    wp => wp.salarySetting?.usePlannerForOvertime
  )
  const plannerOvertimeRelevant =
    anyWorkPeriodUsesPlannerForOvertime || calendarItems.length > 0
  const showPlannedWork = plannerOvertimeRelevant && plannedWorkFilterEnabled
  const restPeriods = getNodes(restData?.allRestPeriods).filter(restPeriod =>
    restPeriod.isWeekRest ? showWeekRest : showDayRest
  )
  const approvalRows = getApprovalRows(
    workPeriods,
    showPlannedWork ? calendarItems : [],
    restRowsAvailable ? restPeriods : []
  )
  const salaryCalculationApprovalRows = approvalRows.filter(
    row => row.user.includeInSalaryCalculation
  )
  const selectedLeaderGroupUsers = new Set(form.formValues.leaderGroupsUsers)
  const selectedLeaderGroup = form.formValues.leaderGroup

  const approvalRowsInScope = salaryCalculationApprovalRows.filter(row => {
    if (!selectedLeaderGroup) return true
    if (row.type === 'restPeriod') return true
    return (
      selectedLeaderGroupUsers.has(row.user.id) ||
      row.workPeriod?.leaderGroup?.id === selectedLeaderGroup
    )
  })

  const displayApprovalRows = approvalRowsInScope.filter(row => {
    if (form.formValues.showResolved) return true
    // A rest follows the shift it hangs off: hiding the resolved shift and
    // leaving its rest behind would strand a band with nothing to connect.
    if (row.type === 'restPeriod')
      return !row.restPeriod.workPeriodBefore.approvedByManager
    return row.type === 'calendarItem' || !row.workPeriod.approvedByManager
  })

  const userTasks = (remainingData?.remainingWorkPeriodResolving ?? []).filter(
    task => task.user.includeInSalaryCalculation
  )

  const remainingWorkPeriodResolvingCount = userTasks.length ?? 0

  const leaderGroupUserIds = (groupId: string | null) =>
    myLeaderGroups.find(g => g.id === groupId)?.users.map(user => user.id) ?? []

  // The user select only offers members of the selected leader group, so a
  // user picked before the group change can fall outside it — drop the
  // selection instead of leaving a value the select can no longer show.
  const changeLeaderGroup = (value: string | null) => {
    const groupUsers = leaderGroupUserIds(value)
    const selectedUser = form.formValues.user
    const keepUser =
      !value || !selectedUser || groupUsers.includes(selectedUser)

    form.updateForm({
      leaderGroup: value,
      leaderGroupsUsers: groupUsers,
      user: keepUser ? selectedUser : null,
    })
  }

  // The approval banner lists everyone in the company, so picking a user from
  // it can point outside the selected group. Clear the group filter instead of
  // selecting a user the select hides and the rows filter away.
  const selectUser = (userId: string) => {
    const selectedGroup = form.formValues.leaderGroup
    const outsideGroup =
      !!selectedGroup && !leaderGroupUserIds(selectedGroup).includes(userId)

    form.updateForm({
      user: userId,
      ...(outsideGroup ? { leaderGroup: null, leaderGroupsUsers: [] } : {}),
    })
  }

  return (
    <Wrapper>
      <InfoContentWrapper>
        <H1>{translations.confirmHours}</H1>
        {infoContent && <InfoButton />}
      </InfoContentWrapper>

      {showRemainingBanner && remainingWorkPeriodResolvingCount > 0 && (
        <div className="mb-4">
          <UserWorkPeriodsWarningCard
            users={userTasks}
            title={
              remainingWorkPeriodResolvingCount === 1
                ? translations.oneUserNeedsApproval
                : translations.nUsersNeedApproval({
                    n: remainingWorkPeriodResolvingCount,
                  })
            }
            onSelectUser={selectUser}
          />
        </div>
      )}
      <Controls>
        <section>
          {canSeeOthers && (
            <Field>
              <label>{translations.user}</label>
              <UserCombobox
                mode="single"
                value={form.formValues.user ?? ''}
                onChange={user => form.updateForm({ user: user || null })}
                filterSalaryResponsible={false}
                filterLeaderGroup
                filterSalaryCalculation
                hideItem={hideUserItem}
                clearable
                className="h-[3.875rem] min-w-[260px]"
              />
            </Field>
          )}
          {hasAccess && (
            <Field>
              <label>{translations.leaderGroups}</label>
              <Combobox
                mode="single"
                value={form.formValues.leaderGroup ?? ''}
                items={
                  myLeaderGroups?.map(group => ({
                    value: group.id,
                    label: group.name,
                  })) ?? []
                }
                clearable
                placeholder={translations.chooseGroup}
                onChange={value => changeLeaderGroup(value || null)}
                className="h-[3.875rem] min-w-[220px]"
              />
            </Field>
          )}

          <Field>
            <label>{translations.showResolved}</label>
            <Checkbox
              checked={form.formValues.showResolved}
              onChange={checked => form.updateForm({ showResolved: checked })}
              boxSize="3.875rem"
              padding="2rem"
              boxBorderRadius="8px"
            />
          </Field>
          {restRowsAvailable && (
            <Field>
              <label>{translations.dayRest}</label>
              <Checkbox
                checked={showDayRest}
                onChange={checked => form.updateForm({ showDayRest: checked })}
                boxSize="3.875rem"
                padding="2rem"
                boxBorderRadius="8px"
              />
            </Field>
          )}
          {restRowsAvailable && (
            <Field>
              <label>{translations.weekRest}</label>
              <Checkbox
                checked={showWeekRest}
                onChange={checked => form.updateForm({ showWeekRest: checked })}
                boxSize="3.875rem"
                padding="2rem"
                boxBorderRadius="8px"
              />
            </Field>
          )}
          {plannerOvertimeRelevant && (
            <Field>
              <label>{translations.showPlannedWork}</label>
              <Checkbox
                checked={plannedWorkFilterEnabled}
                onChange={checked =>
                  form.updateForm({ showPlannedWork: checked })
                }
                boxSize="3.875rem"
                padding="2rem"
                boxBorderRadius="8px"
              />
            </Field>
          )}
        </section>
        <section>
          <Field>
            <label>{translations.month}</label>
            <MonthYearPicker
              value={form.formValues.month}
              onChange={date => form.updateForm({ month: startOfMonth(date) })}
              className="h-[3.875rem] min-w-[240px]"
            />
          </Field>
        </section>
      </Controls>

      {effectiveUser && (
        <SalaryMonthSummaryCards
          month={datetimeStart}
          rangeStart={datetimeStart}
          rangeEnd={datetimeEnd}
          summary={aggregateMonthSummary(
            monthSummary?.monthReport,
            datetimeStart,
            datetimeEnd,
            hasApprovalFlow && form.formValues.onlyApprovedSums
              ? { driverApprove, managerApprove }
              : undefined
          )}
          sumScope={
            hasApprovalFlow
              ? form.formValues.onlyApprovedSums
                ? 'approved'
                : 'all'
              : undefined
          }
          onSumScopeChange={scope =>
            form.updateForm({ onlyApprovedSums: scope === 'approved' })
          }
          loading={!monthSummary?.monthReport}
          collapsed={form.formValues.sumsCollapsed}
          onToggleCollapsed={() =>
            form.updateForm({
              sumsCollapsed: !form.formValues.sumsCollapsed,
            })
          }
        />
      )}

      {isMobileOnly ? (
        <WorkPeriodApproveCards
          approvalRows={displayApprovalRows}
          leaderGroups={myLeaderGroups}
          singleUserSelected={!!form.formValues.user}
          loading={loading}
          initRefetch={refetchAll}
          refetchAfterApproval={refetchApprovalTotals}
        />
      ) : (
        <WorkPeriodApproveTable
          approvalRows={displayApprovalRows}
          leaderGroups={myLeaderGroups}
          showUserColumn={canSeeOthers}
          singleUserSelected={!!form.formValues.user}
          loading={loading}
          initRefetch={refetchAll}
          refetchAfterApproval={refetchApprovalTotals}
        />
      )}
      {infoContent && <InfoModal />}
    </Wrapper>
  )
}
